import React, { useCallback, useEffect, useMemo, useState } from "react";
import { dashboard } from "../../lib/api";
import { formatCurrency, formatDateTime } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { BarChart3, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { toast } from "sonner";

const COMPANY_STYLES = {
  Petrokar: { color: "#2f6fed", soft: "#eaf1ff" },
  FAST: { color: "#17835c", soft: "#e9f7f1" },
  WELL: { color: "#d97706", soft: "#fff4e6" },
  PRK: { color: "#c2413a", soft: "#fdedec" },
};

const METRICS = [
  { key: "nf_empresa", label: "NF gerada pela Empresa", shortLabel: "NF Empresa" },
  { key: "pago_empresa", label: "Pago pela Conterp", shortLabel: "Pago Conterp" },
  { key: "nf_rm", label: "NF gerada pela RM", shortLabel: "NF RM" },
  { key: "pago_rm", label: "Pago a RM", shortLabel: "Pago a RM" },
  { key: "falta_pagar", label: "Falta pagar", shortLabel: "Falta pagar" },
];

const DETAIL_COLUMNS = [
  { key: "numero_af", label: "Número da AF", type: "text" },
  { key: "nf_empresa", label: "NF da Empresa", type: "text" },
  { key: "valor_nf_empresa", label: "NF Empresa (R$)", type: "currency" },
  { key: "pago_conterp", label: "Pago pela Conterp", type: "currency" },
  { key: "nf_rm", label: "NF da RM", type: "text" },
  { key: "valor_nf_rm", label: "NF RM (R$)", type: "currency" },
  { key: "pago_rm", label: "Pago a RM", type: "currency" },
  { key: "falta_pagar", label: "Falta pagar", type: "currency" },
];

function CompanyPanel({ company }) {
  const style = COMPANY_STYLES[company.empresa] || COMPANY_STYLES.Petrokar;
  const chartData = useMemo(
    () => METRICS.map((metric) => ({ nome: metric.shortLabel, valor: company[metric.key] || 0 })),
    [company]
  );

  return (
    <Card className="overflow-hidden" data-testid={`outras-empresas-${company.empresa.toLowerCase()}`}>
      <CardHeader className="py-4 text-white" style={{ backgroundColor: style.color }}>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="font-heading text-xl">{company.empresa.toUpperCase()}</CardTitle>
          <Badge className="border-white/30 bg-white/15 text-white hover:bg-white/15">
            {company.total_registros} registros
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="grid gap-5 p-5 lg:grid-cols-[minmax(260px,0.8fr)_minmax(360px,1.2fr)]">
        <div className="overflow-hidden rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-3 font-semibold">Indicador</th>
                <th className="px-3 py-3 text-right font-semibold">Valor</th>
              </tr>
            </thead>
            <tbody>
              {METRICS.map((metric) => {
                const isBalance = metric.key === "falta_pagar";
                return (
                  <tr
                    key={metric.key}
                    className="border-t"
                    style={isBalance ? { backgroundColor: style.soft } : undefined}
                  >
                    <td className={isBalance ? "px-3 py-3 font-semibold" : "px-3 py-3"}>
                      {metric.label}
                    </td>
                    <td
                      className="px-3 py-3 text-right font-semibold tabular-nums"
                      style={isBalance ? { color: style.color } : undefined}
                    >
                      {formatCurrency(company[metric.key])}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="border-t bg-slate-50 px-3 py-2 text-xs italic text-slate-500">
            Saldo = NF Empresa + NF RM − Pago Conterp − Pago a RM
          </p>
        </div>

        <div className="min-h-[290px]">
          <ResponsiveContainer width="100%" height={290}>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 28 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="nome"
                interval={0}
                angle={-20}
                textAnchor="end"
                height={58}
                tick={{ fontSize: 11 }}
              />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`} />
              <Tooltip formatter={(value) => [formatCurrency(value), "Valor"]} />
              <Bar dataKey="valor" fill={style.color} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h3 className="font-heading text-base font-semibold text-slate-800">
              Lançamentos detalhados
            </h3>
            <span className="text-xs text-slate-500">
              {company.registros?.length || 0} linhas
            </span>
          </div>
          <div className="max-h-[360px] overflow-auto rounded-lg border">
            <table className="w-full min-w-[1120px] text-sm">
              <thead className="sticky top-0 z-10 bg-slate-100">
                <tr className="text-xs uppercase tracking-wide text-slate-600">
                  {DETAIL_COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className={`whitespace-nowrap px-3 py-3 font-semibold ${
                        column.type === "currency" ? "text-right" : "text-left"
                      }`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {company.registros?.length ? (
                  company.registros.map((record, index) => (
                    <tr
                      key={`${company.empresa}-${record.numero_af || "sem-af"}-${index}`}
                      className="border-t even:bg-slate-50/70"
                    >
                      {DETAIL_COLUMNS.map((column) => {
                        const isCurrency = column.type === "currency";
                        const isBalance = column.key === "falta_pagar";
                        return (
                          <td
                            key={column.key}
                            className={`whitespace-nowrap px-3 py-2.5 ${
                              isCurrency ? "text-right tabular-nums" : "text-left"
                            } ${isBalance ? "font-semibold" : ""}`}
                            style={isBalance ? { color: style.color } : undefined}
                          >
                            {isCurrency
                              ? formatCurrency(record[column.key])
                              : record[column.key] || "-"}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={DETAIL_COLUMNS.length} className="px-3 py-8 text-center text-slate-500">
                      Nenhum lançamento encontrado para esta empresa.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function OutrasEmpresasPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response = await dashboard.outrasEmpresas();
      setData(response.data);
    } catch (error) {
      const forbidden = error.response?.status === 403;
      toast.error(forbidden ? "Acesso restrito às contas Admin e Vendas" : "Erro ao carregar Outras Empresas");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="outras-empresas-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <BarChart3 className="h-7 w-7 text-[#ea580c]" />
            <h1 className="font-heading text-3xl font-bold text-foreground">Outras Empresas</h1>
          </div>
          <p className="text-muted-foreground">Gestão de notas fiscais e pagamentos por empresa</p>
          {data?.arquivo_atualizado_em && (
            <p className="mt-1 text-xs text-slate-500">
              Dados do Gestao.xlsx atualizados em {formatDateTime(data.arquivo_atualizado_em)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700">
            <ShieldCheck className="h-4 w-4" />
            Admin e Vendas
          </Badge>
          <Button variant="outline" onClick={handleRefresh} disabled={refreshing} data-testid="outras-empresas-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {data?.empresas?.length ? (
        <div className="grid grid-cols-1 gap-6 2xl:grid-cols-2">
          {data.empresas.map((company) => (
            <CompanyPanel key={company.empresa} company={company} />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Nenhum dado de outras empresas foi encontrado.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
